import { ParamMap } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';

import { AuthUser, OAuthCallbackResult } from '@oneComply/core/models/auth.model';
import { createOAuthState } from '@oneComply/core/utils/oauth-state.util';
import { isProductAccessibleInJwt } from '@oneComply/core/utils/jwt-claims.util';
import { environment } from '@environments/environment';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';
const OAUTH_STATE_KEY = 'oauth_state';
const POST_LOGIN_REDIRECT_KEY = 'oauth_post_login_redirect';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  logout(): void {
    void this.performLogout();
  }

  redirectToLogin(intendedPath = '/dashboard', forcePrompt = false): void {
    if (typeof window === 'undefined') {
      return;
    }

    const state = createOAuthState();
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intendedPath);

    const loginUrl = new URL(environment.authSpaUrl);
    loginUrl.searchParams.set('return_url', this.resolveAuthCallbackUrl());
    loginUrl.searchParams.set('state', state);

    if (forcePrompt) {
      loginUrl.searchParams.set('prompt', 'login');
    }

    window.location.replace(loginUrl.toString());
  }

  handleOAuthCallback(params: ParamMap): OAuthCallbackResult {
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);

    sessionStorage.removeItem(OAUTH_STATE_KEY);

    if (!accessToken) {
      return { success: false, error: 'Token de acesso não recebido.' };
    }

    if (!state || !expectedState || state !== expectedState) {
      this.clearSession();
      return { success: false, error: 'Estado OAuth inválido (CSRF).' };
    }

    this.persistOAuthSession(accessToken, refreshToken);
    return { success: true };
  }

  consumePostLoginRedirect(): string {
    const redirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? '/dashboard';
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return redirect.startsWith('/') ? redirect : '/dashboard';
  }

  recordLoginSession(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>(
      `${environment.apiUrl}/auth/login`,
      {},
    );
  }

  recordLogoutSession(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>(
      `${environment.apiUrl}/auth/logout`,
      { refresh_token: this.getRefreshToken() },
    );
  }

  persistOAuthSession(accessToken: string, refreshToken: string | null): void {
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.setItem(TOKEN_KEY, accessToken);

    if (refreshToken) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  getToken(): string | null {
    const sessionToken = sessionStorage.getItem(TOKEN_KEY);

    if (sessionToken) {
      return sessionToken;
    }

    this.clearLegacyLocalStorageAuth();

    return null;
  }

  isProductAccessible(token?: string | null): boolean {
    const value = token ?? this.getToken();

    if (!value) {
      return false;
    }

    return isProductAccessibleInJwt(value);
  }

  getRefreshToken(): string | null {
    const sessionToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);

    if (sessionToken) {
      return sessionToken;
    }

    return null;
  }

  getStoredUser(): AuthUser | null {
    const userStr = sessionStorage.getItem(USER_KEY);

    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr) as AuthUser;
    } catch {
      return null;
    }
  }

  updateStoredUser(user: AuthUser): void {
    const userStr = JSON.stringify(user);
    sessionStorage.setItem(USER_KEY, userStr);
    localStorage.removeItem(USER_KEY);
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  }

  private async performLogout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    const returnUrl = this.resolvePostLogoutReturnUrl();

    if (this.getToken()) {
      try {
        await firstValueFrom(this.recordLogoutSession());
      } catch {
        // Continua o logout mesmo se a auditoria falhar.
      }
    }

    this.clearSession();

    if (typeof window === 'undefined') {
      return;
    }

    if (refreshToken) {
      await this.revokeRefreshToken(refreshToken);
    }

    window.location.replace(this.buildSsoLogoutUrl(returnUrl));
  }

  private async revokeRefreshToken(refreshToken: string): Promise<void> {
    const body = new URLSearchParams({
      client_id: environment.oauth.clientId,
      token: refreshToken,
      token_type_hint: 'refresh_token',
    });

    try {
      await fetch(`${environment.authBaseUrl}/v1/oauth/token/revoke`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });
    } catch {
      // Continua o logout SSO mesmo se a revogação falhar.
    }
  }

  private buildSsoLogoutUrl(returnUrl: string): string {
    const logoutUrl = new URL(`${environment.authBaseUrl}/sso/logout`);
    logoutUrl.searchParams.set('return_url', returnUrl);
    return logoutUrl.toString();
  }

  private resolveAuthCallbackUrl(): string {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/auth/callback`;
    }

    return environment.authCallbackUrl;
  }

  private resolvePostLogoutReturnUrl(): string {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/admin/login?reauth=1`;
    }

    return `${environment.authCallbackUrl.replace(/\/auth\/callback\/?$/, '/admin/login')}?reauth=1`;
  }

  private clearLegacyLocalStorageAuth(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
}

